import { useState, useEffect, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { useClientLevel } from "../../hooks/useClientLevel";
import { useNavigate } from "react-router-dom";
import baseapi from "../../services/baseapi";
import { showError } from "../../utils/toast";
import { ItemsPerPageDropdown } from "../../components/ItemsPerPageDropdown ";
import AddExternalAssetModal from "./AddExternalAssetModal";
import ScanOptionsModal from "./ScanOptionsModal";
import ColumnVisibilityDropdown, { type ColumnOption } from "../../components/ColumnVisibilityDropdown";
import {
  Search,
  Globe,
  RefreshCw,
  Briefcase,
  Clock,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  ChevronsLeft,
  ChevronLeft,
  ChevronRight,
  ChevronsRight,
  Loader2,
  Network,
  Plus,
  CircleEllipsis,
  Pencil,
  TableOfContents,
  Radio,
  Hash,
  ShieldAlert,
  CheckCircle2,
  XCircle,
  PlayCircle,
  Scan
} from "lucide-react";

interface ExternalAsset {
  id: number;
  client_id: number;
  client_name: string;
  name: string;
  asset_type: string;
  ip_address: string | null;
  domain: string | null;
  fqdn: string | null;
  created: string;
  updated: string;
  resolved_tags?: string;
  tags?: string;
  protocols_performed?: string | string[];
  ports_count?: number;
  info_count?: number;
  low_count?: number;
  medium_count?: number;
  high_count?: number;
  critical_count?: number;
  last_scan: string;
  job_id?: string;
  job_status?: string;
  job_progress?: number;
}

export type ExternalScanColumnKey =
  | "name"
  | "ip_address"
  | "critical_count"
  | "high_count"
  | "medium_count"
  | "low_count"
  | "info_count"
  | "ports_count"
  | "protocols_performed"
  | "last_scan"
  | "job_status"
  | "actions";

export default function ExternalScanTable() {
  const level = useClientLevel();
  const clientId = level?.id;
  const navigate = useNavigate();

  // Table states
  const [assets, setAssets] = useState<ExternalAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState<Record<ExternalScanColumnKey, boolean>>({
    name: true,
    ip_address: true,
    critical_count: true,
    high_count: true,
    medium_count: true,
    low_count: true,
    info_count: true,
    ports_count: true,
    protocols_performed: true,
    last_scan: true,
    job_status: true,
    actions: true,
  });

  const columnOptions: ColumnOption<ExternalScanColumnKey>[] = useMemo(() => [
    { key: "name", label: "Name" },
    { key: "ip_address", label: "IP / Domain" },
    { key: "critical_count", label: "Critical" },
    { key: "high_count", label: "High" },
    { key: "medium_count", label: "Medium" },
    { key: "low_count", label: "Low" },
    { key: "info_count", label: "Info" },
    { key: "ports_count", label: "Ports Count" },
    { key: "protocols_performed", label: "Technologies" },
    { key: "last_scan", label: "Last Scan Date" },
    { key: "job_status", label: "Scan Status" },
    { key: "actions", label: "Action" },
  ], []);

  const vulnColsCount =
    (visibleColumns.critical_count ? 1 : 0) +
    (visibleColumns.high_count ? 1 : 0) +
    (visibleColumns.medium_count ? 1 : 0) +
    (visibleColumns.low_count ? 1 : 0) +
    (visibleColumns.info_count ? 1 : 0);

  const visibleColsCount =
    (visibleColumns.name ? 1 : 0) +
    (visibleColumns.ip_address ? 1 : 0) +
    vulnColsCount +
    (visibleColumns.ports_count ? 1 : 0) +
    (visibleColumns.protocols_performed ? 1 : 0) +
    (visibleColumns.last_scan ? 1 : 0) +
    (visibleColumns.job_status ? 1 : 0) +
    (visibleColumns.actions ? 1 : 0) || 1;

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [totalItems, setTotalItems] = useState(0);

  // Sorting states
  const [sortBy, setSortBy] = useState<string>("updated");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  // Protocols expansion state
  const [expandedProtocols, setExpandedProtocols] = useState<Record<number, boolean>>({});

  // Modal states
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit">("add");
  const [selectedAsset, setSelectedAsset] = useState<ExternalAsset | null>(null);
  const [isScanModalOpen, setIsScanModalOpen] = useState(false);
  const [scanAssetId, setScanAssetId] = useState<number | null>(null);
  const [scanAssetName, setScanAssetName] = useState("");

  // Action Menu Dropdown states
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const menuDropdownRef = useRef<HTMLDivElement | null>(null);

  const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  // Handle debouncing of search term
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 450);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  // Fetch external asset data
  const fetchData = async () => {
    if (!clientId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const skip = (currentPage - 1) * itemsPerPage;
      const queryObj: any = {
        client_id: Number(clientId),
        asset_type: "external_asset",
      };

      if (debouncedSearch.trim()) {
        queryObj.$or = [
          { name: { $ilike: `%${debouncedSearch}%` } },
          { ip_address: { $ilike: `%${debouncedSearch}%` } },
          { domain: { $ilike: `%${debouncedSearch}%` } },
          { fqdn: { $ilike: `%${debouncedSearch}%` } },
        ];
      }

      const params = {
        query: JSON.stringify(queryObj),
        skip,
        limit: itemsPerPage,
        sort_by: sortBy,
        sort_order: sortOrder,
      };

      const response = await baseapi.get("/external_scan", { params });
      if (response.data && response.data.status) {
        setAssets(response.data.data || []);
        setTotalItems(response.data.total || 0);
      } else {
        setAssets([]);
        setTotalItems(0);
      }
    } catch (error: any) {
      showError(error.message || "Failed to load external scan data");
      setAssets([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [clientId, currentPage, itemsPerPage, debouncedSearch, sortBy, sortOrder]);

  // Handle click outside to close the Action Dropdown menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        openMenuId &&
        !target.closest("[data-menu-id]") &&
        menuDropdownRef.current &&
        !menuDropdownRef.current.contains(target)
      ) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openMenuId]);

  const handleRefresh = () => {
    fetchData();
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
    setCurrentPage(1);
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (sortBy !== field) {
      return <ArrowUpDown className="w-3 h-3 text-gray-500 opacity-50" />;
    }
    return sortOrder === "asc" ? (
      <ArrowUp className="w-3 h-3 text-orange-400" />
    ) : (
      <ArrowDown className="w-3 h-3 text-orange-400" />
    );
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "-";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateStr;
    }
  };

  const renderStatusBadge = (status?: string) => {
    if (!status) {
      return (
        <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-gray-800 text-gray-400 border border-gray-700 whitespace-nowrap">
          <Clock className="w-2.5 h-2.5" /> Idle
        </div>
      );
    }

    const s = status.toLowerCase();
    if (s === "completed" || s === "success") {
      return (
        <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 whitespace-nowrap">
          <CheckCircle2 className="w-2.5 h-2.5" /> Completed
        </div>
      );
    }

    if (s === "running" || s === "in_progress") {
      return (
        <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-blue-500/10 text-blue-400 border border-blue-500/20 whitespace-nowrap">
          <Loader2 className="w-2.5 h-2.5 animate-spin" /> Scanning
        </div>
      );
    }

    if (s === "failed" || s === "error") {
      return (
        <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-red-500/10 text-red-400 border border-red-500/20 whitespace-nowrap">
          <XCircle className="w-2.5 h-2.5" /> Failed
        </div>
      );
    }

    return (
      <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-gray-800 text-gray-300 border border-gray-700 whitespace-nowrap">
        <Clock className="w-2.5 h-2.5" /> {status}
      </div>
    );
  };

  const renderProgress = (progress?: number) => {
    if (progress === undefined || progress === null) return null;

    const value = Math.max(0, Math.min(100, progress));

    let colorClass = "bg-indigo-500";
    if (value === 100) colorClass = "bg-emerald-500";
    if (value > 0 && value < 100) colorClass = "bg-blue-500";
    if (value === 0) colorClass = "bg-gray-600";

    return (
      <div className="w-full max-w-[120px] flex items-center gap-2">
        <div className="w-full bg-gray-800 rounded-full h-1 border border-gray-700/50 overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ease-in-out ${colorClass}`}
            style={{ width: `${value}%` }}
          />
        </div>
        <span className={`text-[8px] font-black w-6 text-right tracking-tight ${value === 100 ? 'text-emerald-400' : 'text-gray-400'}`}>
          {value}%
        </span>
      </div>
    );
  };

  const getIpOrDomain = (item: ExternalAsset) => {
    return item.domain || item.fqdn || item.ip_address || "-";
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, id: number) => {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    setDropdownPosition({
      top: rect.bottom + window.scrollY + 2,
      left: rect.left + window.scrollX - 120,
    });
    setOpenMenuId(openMenuId === id ? null : id);
  };

  useEffect(() => {
    if (!clientId || level?.name === "global") {
      navigate("/dashboard", { replace: true });
    }
  }, [clientId, level, navigate]);

  // If no client is selected or global is active
  if (!clientId || level?.name === "global") {
    return null;
  }

  const hasRowSpan2 = vulnColsCount > 0;

  return (
    <div className="bg-gray-900 p-3 pb-20 min-h-screen text-gray-200">
      <div className="w-full mx-auto">
        {/* Top Header Row */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-2">
          <div className="flex items-center gap-1.5">
            <div className="bg-orange-600 p-1.5 rounded shadow-lg">
              <Globe className="w-4 h-4 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-lg text-gray-200 leading-none">External Scan</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="rounded-lg bg-gray-800 border border-gray-700 text-gray-300 p-2 hover:bg-gray-700 transition-colors"
            >
              <RefreshCw className={`w-3.5 h-3.5 font-semibold ${loading ? "animate-spin" : ""}`} />
            </button>

            <div className="relative w-full sm:w-56">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-200 w-3.5 h-3.5" />
              <input
                type="text"
                placeholder="Search by name, IP, or domain..."
                className="w-full bg-gray-800/50 border border-gray-700 rounded-lg pl-8 pr-3 py-1.5 text-[11px] text-white focus:border-orange-500 outline-none transition-all"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <ColumnVisibilityDropdown
              columns={columnOptions}
              visibleColumns={visibleColumns}
              onChange={setVisibleColumns}
            />
            <button
              onClick={() => {
                setSelectedAsset(null);
                setModalMode("add");
                setIsAddModalOpen(true);
              }}
              className="flex items-center gap-1.5 bg-orange-600 rounded-lg px-4 py-1.5 text-[11px] text-white font-bold hover:bg-orange-700 shadow-lg shadow-orange-900/20 transition-all"
            >
              <Plus className="w-4 h-4" /> Add Asset
            </button>
            <button
              onClick={() => navigate("/external-scan/jobs?job_type=external_scan")}
              className="flex items-center gap-1.5 bg-gray-800 border border-gray-700 rounded-lg px-4 py-1.5 text-[11px] text-white font-bold hover:bg-gray-700 transition-all"
            >
              <Briefcase className="w-4 h-4 text-orange-500" /> Jobs
            </button>
          </div>
        </div>

        {/* Table View Body */}
        <div className="bg-gray-800/40 border border-gray-700 rounded-xl overflow-hidden shadow-2xl">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs border-collapse">
              <thead className="bg-gray-800/80 sticky top-0 z-10">
                <tr className="border-b border-gray-700/50">
                  {/* 1. Name */}
                  {visibleColumns.name && (
                    <th
                      rowSpan={hasRowSpan2 ? 2 : 1}
                      onClick={() => handleSort("name")}
                      className="px-2 py-1.5 text-[10px] font-bold text-gray-100 uppercase tracking-widest cursor-pointer group hover:bg-gray-700/50 transition-colors text-left"
                    >
                      <div className="flex items-center gap-2">
                        <Globe size={14} className="text-blue-400" />
                        Name
                        <SortIcon field="name" />
                      </div>
                    </th>
                  )}
                  {/* 2. IP Address / Domain */}
                  {visibleColumns.ip_address && (
                    <th
                      rowSpan={hasRowSpan2 ? 2 : 1}
                      onClick={() => handleSort("ip_address")}
                      className="px-2 py-1.5 text-[10px] font-bold text-gray-100 uppercase tracking-widest cursor-pointer group hover:bg-gray-700/50 transition-colors text-left"
                    >
                      <div className="flex items-center gap-2">
                        <Network size={14} className="text-cyan-400" />
                        IP Address / Domain
                        <SortIcon field="ip_address" />
                      </div>
                    </th>
                  )}
                  {/* 3. Vulnerabilities */}
                  {vulnColsCount > 0 && (
                    <th
                      colSpan={vulnColsCount}
                      className="px-2 py-1 text-[10px] font-bold text-gray-100 uppercase tracking-widest text-center border-b border-gray-700/50"
                    >
                      <div className="flex items-center justify-center gap-2">
                        <ShieldAlert size={14} className="text-red-400" />
                        Vulnerabilities
                      </div>
                    </th>
                  )}
                  {/* 4. Ports Count */}
                  {visibleColumns.ports_count && (
                    <th
                      rowSpan={hasRowSpan2 ? 2 : 1}
                      onClick={() => handleSort("ports_count")}
                      className="px-2 py-1.5 text-[10px] font-bold text-gray-100 uppercase tracking-widest cursor-pointer group hover:bg-gray-700/50 transition-colors text-center relative"
                    >
                      <div className="flex items-center justify-center gap-2 pr-4">
                        <Hash size={14} className="text-sky-400" />
                        Ports Count
                      </div>
                      <span className="absolute right-1 top-1/2 -translate-y-1/2">
                        <SortIcon field="ports_count" />
                      </span>
                    </th>
                  )}
                  {/* 5. Protocols */}
                  {visibleColumns.protocols_performed && (
                    <th
                      rowSpan={hasRowSpan2 ? 2 : 1}
                      onClick={() => handleSort("protocols_performed")}
                      className="px-2 py-1.5 text-[10px] font-bold text-gray-100 uppercase tracking-widest cursor-pointer group hover:bg-gray-700/50 transition-colors text-left"
                    >
                      <div className="flex items-center gap-2">
                        <Radio size={14} className="text-purple-400" />
                        Technologies
                        <SortIcon field="protocols_performed" />
                      </div>
                    </th>
                  )}
                  {/* 6. Last Scan Date */}
                  {visibleColumns.last_scan && (
                    <th
                      rowSpan={hasRowSpan2 ? 2 : 1}
                      onClick={() => handleSort("last_scan")}
                      className="px-2 py-1.5 text-[10px] font-bold text-gray-100 uppercase tracking-widest cursor-pointer group hover:bg-gray-700/50 transition-colors text-left"
                    >
                      <div className="flex items-center gap-2">
                        <Clock size={14} className="text-orange-400" />
                        Last Scan Date
                        <SortIcon field="last_scan" />
                      </div>
                    </th>
                  )}
                  {/* 7. Scan Status */}
                  {visibleColumns.job_status && (
                    <th
                      rowSpan={hasRowSpan2 ? 2 : 1}
                      onClick={() => handleSort("job_status")}
                      className="px-2 py-1.5 text-[10px] font-bold text-gray-100 uppercase tracking-widest cursor-pointer group hover:bg-gray-700/50 transition-colors text-left"
                    >
                      <div className="flex items-center gap-2">
                        <PlayCircle size={14} className="text-blue-400" />
                        Scan Status
                        <SortIcon field="job_status" />
                      </div>
                    </th>
                  )}
                  {/* 8. Action */}
                  {visibleColumns.actions && (
                    <th
                      rowSpan={hasRowSpan2 ? 2 : 1}
                      className="px-2 py-1.5 text-[10px] font-bold text-gray-100 uppercase tracking-widest text-center"
                    >
                      <div className="flex items-center justify-center gap-2">
                        <TableOfContents size={14} className="text-gray-500" />
                        Action
                      </div>
                    </th>
                  )}
                </tr>
                {vulnColsCount > 0 && (
                  <tr className="border-b border-gray-700/50">
                    {visibleColumns.critical_count && (
                      <th className="px-1 py-1 text-[9px] font-bold text-red-400 uppercase tracking-wider text-center cursor-pointer hover:bg-gray-700/30 min-w-[70px] relative" onClick={() => handleSort("critical_count")}>
                        <div className="inline-block">Crit</div>
                        <span className="absolute right-1 top-1/2 -translate-y-1/2">
                          <SortIcon field="critical_count" />
                        </span>
                      </th>
                    )}
                    {visibleColumns.high_count && (
                      <th className="px-1 py-1 text-[9px] font-bold text-orange-400 uppercase tracking-wider text-center cursor-pointer hover:bg-gray-700/30 min-w-[70px] relative" onClick={() => handleSort("high_count")}>
                        <div className="inline-block">High</div>
                        <span className="absolute right-1 top-1/2 -translate-y-1/2">
                          <SortIcon field="high_count" />
                        </span>
                      </th>
                    )}
                    {visibleColumns.medium_count && (
                      <th className="px-1 py-1 text-[9px] font-bold text-yellow-400 uppercase tracking-wider text-center cursor-pointer hover:bg-gray-700/30 min-w-[70px] relative" onClick={() => handleSort("medium_count")}>
                        <div className="inline-block">Med</div>
                        <span className="absolute right-1 top-1/2 -translate-y-1/2">
                          <SortIcon field="medium_count" />
                        </span>
                      </th>
                    )}
                    {visibleColumns.low_count && (
                      <th className="px-1 py-1 text-[9px] font-bold text-blue-400 uppercase tracking-wider text-center cursor-pointer hover:bg-gray-700/30 min-w-[70px] relative" onClick={() => handleSort("low_count")}>
                        <div className="inline-block">Low</div>
                        <span className="absolute right-1 top-1/2 -translate-y-1/2">
                          <SortIcon field="low_count" />
                        </span>
                      </th>
                    )}
                    {visibleColumns.info_count && (
                      <th className="px-1 py-1 text-[9px] font-bold text-gray-400 uppercase tracking-wider text-center cursor-pointer hover:bg-gray-700/30 min-w-[70px] relative" onClick={() => handleSort("info_count")}>
                        <div className="inline-block">Info</div>
                        <span className="absolute right-1 top-1/2 -translate-y-1/2">
                          <SortIcon field="info_count" />
                        </span>
                      </th>
                    )}
                  </tr>
                )}
              </thead>
              <tbody className="divide-y divide-gray-700/30">
                {loading ? (
                  <tr>
                    <td colSpan={visibleColsCount} className="py-16 text-center text-gray-200 font-bold uppercase tracking-widest italic">
                      <div className="flex items-center justify-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin text-orange-500" />
                        Loading assets
                      </div>
                    </td>
                  </tr>
                ) : assets.length === 0 ? (
                  <tr>
                    <td colSpan={visibleColsCount} className="py-16 text-center text-gray-500 font-bold uppercase tracking-widest italic">
                      No matching assets found.
                    </td>
                  </tr>
                ) : (
                  assets.map((item) => (
                    <tr
                      key={item.id}
                      onClick={() => {
                        if (item.job_id) {
                          navigate(`/external-scan/results/${item.id}?jobid=${item.job_id}`);
                        } else {
                          showError("No scan results available yet. Run a scan first.");
                        }
                      }}
                      className="hover:bg-gray-800/60 transition-colors group border-b border-gray-700/30 cursor-pointer"
                    >
                      {/* 1. Name */}
                      {visibleColumns.name && (
                        <td className="px-1 py-0.5">
                          <span className="font-bold text-gray-200" title={item.name}>
                            {item.name || "-"}
                          </span>
                        </td>
                      )}

                      {/* 2. IP / Domain */}
                      {visibleColumns.ip_address && (
                        <td className="px-1 py-0.5 font-mono">
                          <span className="text-gray-300 text-[11px] font-bold leading-none">
                            {getIpOrDomain(item)}
                          </span>
                        </td>
                      )}

                      {/* 3. Vulnerability Severities (Critical, High, Medium, Low, Info) */}
                      {visibleColumns.critical_count && (
                        <td className="px-1 py-0.5 text-center">
                          <span className={item.critical_count ? "text-[10px] font-bold font-mono bg-red-500/10 text-red-400 border border-red-500/20 px-1.5 py-0.5 rounded" : "text-[10px] font-mono text-gray-600 font-semibold"}>
                            {item.critical_count ?? 0}
                          </span>
                        </td>
                      )}
                      {visibleColumns.high_count && (
                        <td className="px-1 py-0.5 text-center">
                          <span className={item.high_count ? "text-[10px] font-bold font-mono bg-orange-500/10 text-orange-400 border border-orange-500/20 px-1.5 py-0.5 rounded" : "text-[10px] font-mono text-gray-600 font-semibold"}>
                            {item.high_count ?? 0}
                          </span>
                        </td>
                      )}
                      {visibleColumns.medium_count && (
                        <td className="px-1 py-0.5 text-center">
                          <span className={item.medium_count ? "text-[10px] font-bold font-mono bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 px-1.5 py-0.5 rounded" : "text-[10px] font-mono text-gray-600 font-semibold"}>
                            {item.medium_count ?? 0}
                          </span>
                        </td>
                      )}
                      {visibleColumns.low_count && (
                        <td className="px-1 py-0.5 text-center">
                          <span className={item.low_count ? "text-[10px] font-bold font-mono bg-blue-500/10 text-blue-400 border border-blue-500/20 px-1.5 py-0.5 rounded" : "text-[10px] font-mono text-gray-600 font-semibold"}>
                            {item.low_count ?? 0}
                          </span>
                        </td>
                      )}
                      {visibleColumns.info_count && (
                        <td className="px-1 py-0.5 text-center">
                          <span className={item.info_count ? "text-[10px] font-bold font-mono bg-gray-500/10 text-gray-400 border border-gray-500/20 px-1.5 py-0.5 rounded" : "text-[10px] font-mono text-gray-600 font-semibold"}>
                            {item.info_count ?? 0}
                          </span>
                        </td>
                      )}

                      {/* 4. Ports Count */}
                      {visibleColumns.ports_count && (
                        <td className="px-1 py-0.5 text-center">
                          <span className="text-[10px] text-sky-400 font-semibold bg-sky-500/10 px-2 py-0.5 rounded border border-sky-500/20 font-mono">
                            {item.ports_count !== undefined && item.ports_count !== null ? item.ports_count : 0}
                          </span>
                        </td>
                      )}

                      {/* 5. Protocols */}
                      {visibleColumns.protocols_performed && (
                        <td className="px-1 py-0.5">
                          <div className="flex flex-wrap gap-1">
                            {(() => {
                              const parsedProtocols = (() => {
                                try {
                                  if (!item.protocols_performed) return [];
                                  return Array.isArray(item.protocols_performed)
                                    ? item.protocols_performed
                                    : JSON.parse(item.protocols_performed);
                                } catch {
                                  return [];
                                }
                              })();
                              return parsedProtocols.length > 0 ? (
                                <>
                                  {(expandedProtocols[item.id]
                                    ? parsedProtocols
                                    : parsedProtocols.slice(0, 3)
                                  ).map((p: string, idx: number) => (
                                    <span
                                      key={idx}
                                      className="text-[9px] font-mono bg-gray-700/50 border border-gray-600/50 text-gray-200 font-semibold px-1.5 py-0.5 rounded uppercase"
                                    >
                                      {p}
                                    </span>
                                  ))}

                                  {parsedProtocols.length > 3 && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setExpandedProtocols((prev) => ({
                                          ...prev,
                                          [item.id]: !prev[item.id],
                                        }));
                                      }}
                                      className="text-[9px] text-blue-400 hover:text-blue-300 font-semibold"
                                    >
                                      {expandedProtocols[item.id]
                                        ? "Show less"
                                        : `(+${parsedProtocols.length - 3} more)`}
                                    </button>
                                  )}
                                </>
                              ) : (
                                <span className="text-gray-600 font-mono">-</span>
                              );
                            })()}
                          </div>
                        </td>
                      )}

                      {/* 6. Last Scan Date */}
                      {visibleColumns.last_scan && (
                        <td className="px-1 py-0.5">
                          <span className="text-[10px] text-gray-100 font-medium">
                            {formatDate(item.last_scan)}
                          </span>
                        </td>
                      )}

                      {/* 7. Scan Status */}
                      {visibleColumns.job_status && (
                        <td className="px-1 py-0.5">
                          <div className="flex flex-col gap-1 justify-center">
                            {renderStatusBadge(item.job_status)}
                            {item.job_status === "running" && renderProgress(item.job_progress)}
                          </div>
                        </td>
                      )}

                      {/* 8. Action */}
                      {visibleColumns.actions && (
                        <td className="px-1 py-0.5 text-center">
                          <button
                            data-menu-id={item.id}
                            onClick={(e) => handleMenuOpen(e, item.id)}
                            className="p-1.5 hover:bg-gray-700 rounded-lg text-gray-100 hover:text-white transition-colors"
                          >
                            <CircleEllipsis className="w-4.5 h-4.5" />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Footer */}
          <div className="bg-gray-800/50 border-t border-gray-700 px-6 py-3 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <ItemsPerPageDropdown
                value={itemsPerPage}
                onChange={(val) => {
                  setItemsPerPage(val);
                  setCurrentPage(1);
                }}
              />
              <span className="text-[10px] text-gray-200 font-bold uppercase tracking-widest">
                <span>{startIndex + 1}</span> - <span>{Math.min(endIndex, totalItems)}</span> OF {totalItems}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="p-1.5 text-gray-500 hover:text-white disabled:opacity-30"
                title="First Page"
              >
                <ChevronsLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="p-1.5 text-gray-500 hover:text-white disabled:opacity-30"
                title="Previous Page"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <div className="px-3 py-1 bg-orange-600 rounded-lg text-white text-[11px] font-bold mx-1.5 shadow-lg shadow-orange-900/20">
                {currentPage}
              </div>
              <button
                onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="p-1.5 text-gray-500 hover:text-white disabled:opacity-30"
                title="Next Page"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="p-1.5 text-gray-500 hover:text-white disabled:opacity-30"
                title="Last Page"
              >
                <ChevronsRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Add/Edit External Asset Modal Component */}
      <AddExternalAssetModal
        open={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false);
          setSelectedAsset(null);
        }}
        onSuccess={fetchData}
        clientId={clientId}
        mode={modalMode}
        asset={selectedAsset}
      />

      {/* Scan Options Modal Component */}
      <ScanOptionsModal
        open={isScanModalOpen}
        onClose={() => {
          setIsScanModalOpen(false);
          setScanAssetId(null);
          setScanAssetName("");
        }}
        assetId={scanAssetId}
        assetName={scanAssetName}
      />

      {/* Actions Portal Dropdown Menu */}
      {openMenuId && createPortal(
        <div
          ref={menuDropdownRef}
          className="fixed w-40 bg-gray-900 border border-gray-700 rounded-xl shadow-2xl z-[9999] overflow-hidden py-1.5"
          style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
        >
          <button
            onClick={() => {
              const matched = assets.find((a) => a.id === openMenuId);
              if (matched) {
                setSelectedAsset(matched);
                setModalMode("edit");
                setIsAddModalOpen(true);
              }
              setOpenMenuId(null);
            }}
            className="w-full text-left px-4 py-2.5 text-[11px] text-gray-300 hover:bg-gray-800 flex items-center gap-3 border-b border-gray-800 transition-colors"
          >
            <Pencil className="w-3.5 h-3.5 text-blue-400" />
            Edit Asset
          </button>
          <button
            onClick={() => {
              const matched = assets.find((a) => a.id === openMenuId);
              if (matched) {
                setScanAssetId(matched.id);
                setScanAssetName(matched.name);
                setIsScanModalOpen(true);
              }
              setOpenMenuId(null);
            }}
            className="w-full text-left px-4 py-2.5 text-[11px] text-gray-300 hover:bg-gray-800 flex items-center gap-3 transition-colors"
          >
            <Scan className="w-3.5 h-3.5 text-emerald-400" />
            Initiate Scan
          </button>
        </div>,
        document.body
      )}
    </div>
  );
}
